import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { useState } from 'react'
import type { CreateStatusLevelModel, StatusLevelModel } from '@/api/model'
import { validateStatusLevel, type StatusLevelValidationErrors } from './statusLevelValidation'
import { IconColorPicker } from '@/components/common/IconColorPicker.tsx'
import {
  usePatchApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevelsStatusLevelIdIcon
} from "@/api/generated/company-management-user/company-management-user.ts";

const DEFAULT_LEVEL_COLOR = '#FFD700'

interface FormValues {
  name: string
  colorHex: string
  iconName: string | null
  payoutPoints: string
  minPoints: string
  maxPoints: string
}

const defaultValues: FormValues = {
  name: '',
  colorHex: DEFAULT_LEVEL_COLOR,
  iconName: null,
  payoutPoints: '',
  minPoints: '',
  maxPoints: '',
}

interface Props {
  onClose: () => void
  onSave: (
    data: CreateStatusLevelModel,
    id: string | undefined,
    onSuccess?: (savedId: string) => Promise<void>,
  ) => void
  isSaving?: boolean
  /** Needed for the icon upload endpoint. */
  serviceModuleId: number
  /** Level to edit; missing = create. */
  level?: StatusLevelModel
  /** The *other* levels — for the overlap check. */
  siblings: StatusLevelModel[]
}

/**
 * Create/edit a status level in a dialog (one component for both, distinguished
 * via `level`). Numeric fields are kept as strings and parsed with
 * `parseIntegerInput`; empty `maxPoints` = open upper end (∞). Validation runs
 * purely client-side beforehand (`validateStatusLevel`), the backend re-validates.
 * Icon + color via the shared `IconColorPicker`; image upload is buffered and
 * sent after the main save so the object exists before the icon is uploaded.
 */
export function StatusLevelDialog({ onClose, onSave, isSaving, serviceModuleId, level, siblings }: Props) {
  const { t } = useTranslation(['service-modules-status', 'common'])

  const { register, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: level
      ? {
        name: level.name ?? '',
        colorHex: level.colorHex ?? DEFAULT_LEVEL_COLOR,
        iconName: level.iconName ?? null,
        payoutPoints: level.payoutPoints.toString(),
        minPoints: level.minPoints.toString(),
        maxPoints: level.maxPoints?.toString() ?? '',
      }
      : defaultValues,
  })

  const [iconUrl, setIconUrl] = useState<string | null>(level?.iconUrl ?? null)
  const [iconBusy, setIconBusy] = useState(false)
  const [pendingIconFile, setPendingIconFile] = useState<File | null>(null)
  const [pendingDelete, setPendingDelete] = useState(false)
  const [errors, setErrors] = useState<StatusLevelValidationErrors>({})

  const { mutateAsync: patchLevelIcon } =
    usePatchApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevelsStatusLevelIdIcon()

  const isEdit = !!level
  const colorHex = watch('colorHex')
  const iconName = watch('iconName')
  const maxPoints = watch('maxPoints')
  const maxPointsIsOpenEnded = maxPoints.trim().length === 0

  function handleUpload(file: File, dataUrl: string) {
    setPendingIconFile(file)
    setPendingDelete(false)
    setIconUrl(dataUrl)
  }

  function handleDelete() {
    if (pendingIconFile) {
      setPendingIconFile(null)
      setIconUrl(level?.iconUrl ?? null)
      return
    }
    setPendingDelete(true)
    setIconUrl(null)
  }

  async function flushIconChange(savedLevelId: string) {
    if (!pendingIconFile && !pendingDelete) return
    setIconBusy(true)
    try {
      const newUrl = await patchLevelIcon({
        serviceModuleId,
        statusLevelId: savedLevelId,
        data: pendingIconFile ? { File: pendingIconFile } : undefined,
      })
      setIconUrl(newUrl.data ?? null)
      setPendingIconFile(null)
      setPendingDelete(false)
    } finally {
      setIconBusy(false)
    }
  }

  const onSubmit = (values: FormValues) => {
    const candidate = {
      name: values.name,
      payoutPoints: parseInt(values.payoutPoints, 10),
      minPoints: parseInt(values.minPoints, 10),
      maxPoints: maxPointsIsOpenEnded ? null : parseInt(values.maxPoints, 10),
      maxPointsIsOpenEnded,
    }
    const validationErrors = validateStatusLevel(candidate, siblings)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    const payload: CreateStatusLevelModel = {
      name: values.name.trim(),
      colorHex: values.colorHex,
      iconName: values.iconName,
      payoutPoints: candidate.payoutPoints,
      minPoints: candidate.minPoints,
      maxPoints: candidate.maxPoints,
    }

    onSave(payload, level?.id, flushIconChange)
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('dialog.editTitle') : t('dialog.createTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="status-level-name">{t('dialog.nameLabel')}</Label>
            <Input
              id="status-level-name"
              {...register('name')}
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="text-xs text-destructive">{t(errors.name)}</p>}
          </div>

          <IconColorPicker
            iconName={iconName}
            colorHex={colorHex}
            iconUrl={iconUrl}
            busy={iconBusy}
            uploadDisabled={false}
            onIconChange={(name) => setValue('iconName', name)}
            onColorChange={(hex) => setValue('colorHex', hex)}
            onUpload={handleUpload}
            onDelete={handleDelete}
          />

          <div className="flex gap-4">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="status-level-min">{t('dialog.minPointsLabel')}</Label>
              <Input
                id="status-level-min"
                inputMode="numeric"
                {...register('minPoints')}
                aria-invalid={!!errors.minPoints}
              />
              {errors.minPoints && <p className="text-xs text-destructive">{t(errors.minPoints)}</p>}
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="status-level-max">{t('dialog.maxPointsLabel')}</Label>
              <Input
                id="status-level-max"
                inputMode="numeric"
                placeholder="∞"
                {...register('maxPoints')}
                aria-invalid={!!errors.maxPoints}
              />
              {errors.maxPoints ? (
                <p className="text-xs text-destructive">{t(errors.maxPoints)}</p>
              ) : maxPointsIsOpenEnded ? (
                <p className="text-xs text-muted-foreground">{t('dialog.openEndedHint')}</p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="status-level-payout">{t('dialog.payoutPointsLabel')}</Label>
            <Input
              id="status-level-payout"
              inputMode="numeric"
              {...register('payoutPoints')}
              aria-invalid={!!errors.payoutPoints}
            />
            {errors.payoutPoints && <p className="text-xs text-destructive">{t(errors.payoutPoints)}</p>}
          </div>

          {errors.range && <p className="text-xs text-destructive">{t(errors.range)}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isSaving || iconBusy}>
            {t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
